// Returns a decorator that debounces the request to a function

interface Params {
  timeout?: number;
  aggregate?: boolean;
  batch?: number;
}

const DEFAULT_DEBOUNCE_TIMEOUT = 200;

function debounceFactory<P, R>(
  { timeout = DEFAULT_DEBOUNCE_TIMEOUT, aggregate = false, batch = 0 }: Params,
  fn: (...args: P[]) => Promise<R>,
) {
  // create a scoped timer
  let timer: ReturnType<typeof setTimeout> | null = null;
  // param buffer
  const params: P[] = [];
  let previousReject: null | ((reason?: unknown) => void) = null;
  // debounce wrapped function with appropriate context bindings
  return function value(this: unknown, ...args: P[]): Promise<R> {
    // Clear previous timeout and start over
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    if (previousReject !== null) {
      previousReject(null);
      previousReject = null;
    }

    // Aggregate variadic params
    if (aggregate === true) {
      params.push(...args);
    }

    if (aggregate === true && batch > 0 && params.length === batch) {
      const result = fn.apply(this, params);
      params.splice(0, params.length);

      return Promise.resolve(result);
    } else {
      return new Promise((resolve, reject) => {
        previousReject = reject;
        // rebind the new debounce function and arguments
        timer = setTimeout(() => {
          if (aggregate === true) {
            resolve(fn.apply(this, params));
          } else {
            resolve(fn.apply(this, args));
          }

          params.splice(0, params.length);
          timer = null;
        }, timeout);
      });
    }
  };
}

export default function decorator<T, P, R>({
  timeout = DEFAULT_DEBOUNCE_TIMEOUT,
  aggregate = false,
  batch = 0,
}: Params) {
  return function decorate(
    _targetClass: T,
    _propertyKey: string,
    // { enumerable, value: method }: TypedPropertyDescriptor<T>,
    descriptor: PropertyDescriptor,
  ) {
    if (descriptor.value === undefined) {
      return;
    }
    const configurable = true;
    const value = debounceFactory<P, R>({ timeout, aggregate, batch }, descriptor.value);

    return <PropertyDescriptor>{
      ...descriptor,
      configurable,
      value,
    };
  };
}
